import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { productsApi, wishlistApi } from '../lib/api';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LanguageContext';
import { Heart, ShoppingBag, ArrowLeft, Check, Minus, Plus } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import ProductReviews, { Stars } from '../components/ProductReviews';

export default function ProductPage() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { addToCart } = useCart();
  const { toast } = useToast();
  const { t, tField } = useLang();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [isInWishlist, setIsInWishlist] = useState(false);
  const [addingToCart, setAddingToCart] = useState(false);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await productsApi.getOne(productId);
        setProduct(response.data);
        if (response.data.sizes?.length > 0) {
          setSelectedSize(response.data.sizes[0]);
        }
        if (response.data.colors?.length > 0) {
          setSelectedColor(response.data.colors[0]);
        }
      } catch (error) {
        console.error('Error fetching product:', error);
        toast({
          title: 'Errore',
          description: 'Prodotto non trovato',
          variant: 'destructive',
        });
        navigate('/shop');
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [productId, navigate, toast]);

  const handleAddToCart = async () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    if (!selectedSize || !selectedColor) {
      toast({
        title: 'Seleziona opzioni',
        description: 'Scegli taglia e colore',
        variant: 'destructive',
      });
      return;
    }

    setAddingToCart(true);
    const result = await addToCart({
      product_id: product.product_id,
      quantity,
      size: selectedSize,
      color: selectedColor,
    });

    if (result.success) {
      toast({
        title: 'Aggiunto al carrello',
        description: `${product.name} aggiunto con successo`,
      });
    } else {
      toast({
        title: 'Errore',
        description: result.error,
        variant: 'destructive',
      });
    }
    setAddingToCart(false);
  };

  const handleToggleWishlist = async () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    try {
      if (isInWishlist) {
        await wishlistApi.remove(product.product_id);
        setIsInWishlist(false);
        toast({ title: 'Rimosso dalla wishlist' });
      } else {
        await wishlistApi.add(product.product_id);
        setIsInWishlist(true);
        toast({ title: 'Aggiunto alla wishlist' });
      }
    } catch (error) {
      toast({
        title: 'Errore',
        description: 'Impossibile aggiornare la wishlist',
        variant: 'destructive',
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 pb-16 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12">
            <div className="aspect-[3/4] bg-[#E5E0D8] animate-pulse" />
            <div className="space-y-4">
              <div className="h-8 bg-[#E5E0D8] w-3/4 animate-pulse" />
              <div className="h-6 bg-[#E5E0D8] w-1/4 animate-pulse" />
              <div className="h-24 bg-[#E5E0D8] animate-pulse" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) return null;

  return (
    <div className="min-h-screen pt-24 pb-16 px-6 md:px-12 lg:px-24">
      <div className="max-w-7xl mx-auto">
        {/* Breadcrumb */}
        <Link
          to="/shop"
          data-testid="back-to-shop-link"
          className="inline-flex items-center gap-2 text-[#57534E] hover:text-[#1C1917] mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          {t({ it: 'Torna allo Shop', en: 'Back to Shop' })}
        </Link>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Product Image */}
          <div className="aspect-[3/4] bg-[#F2EFE9] overflow-hidden">
            <img
              src={product.images[0] || 'https://via.placeholder.com/600x800'}
              alt={product.name}
              className="w-full h-full object-cover"
            />
          </div>

          {/* Product Details */}
          <div>
            <p className="overline mb-2">{product.category}</p>
            {product.preloved && (
              <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="preloved-badges">
                <span className="bg-[#4A5D23] text-white text-xs px-3 py-1 uppercase tracking-wider">
                  Pre-Loved
                </span>
                <span className="bg-[#F2EFE9] text-[#1C1917] text-xs px-3 py-1 uppercase tracking-wider border border-[#E5E0D8]">
                  {product.condition || t({ it: 'Rigenerato e Certificato', en: 'Regenerated & Certified' })}
                </span>
                <span className="bg-[#F2EFE9] text-[#1C1917] text-xs px-3 py-1 uppercase tracking-wider border border-[#E5E0D8]">
                  {t({ it: 'Pezzo Unico', en: 'One of a Kind' })}
                </span>
              </div>
            )}
            <h1 className="text-3xl md:text-4xl font-light tracking-tight mb-4">
              {tField(product, 'name')}
            </h1>
            {product.rating_count > 0 && (
              <div className="flex items-center gap-2 mb-4" data-testid="product-rating-summary">
                <Stars rating={product.rating_avg} />
                <span className="text-sm text-[#57534E]">
                  {product.rating_avg} ({product.rating_count})
                </span>
              </div>
            )}
            <p className="text-2xl text-[#4A5D23] font-medium mb-6">
              €{product.price.toFixed(2)}
            </p>
            <p className="text-[#57534E] leading-relaxed mb-8">
              {tField(product, 'description')}
            </p>

            {/* Size Selection */}
            {product.sizes?.length > 0 && (
              <div className="mb-6">
                <label className="block font-medium mb-3">{t({ it: 'Taglia', en: 'Size' })}</label>
                <div className="flex flex-wrap gap-2">
                  {product.sizes.map((size) => (
                    <button
                      key={size}
                      onClick={() => setSelectedSize(size)}
                      data-testid={`size-${size}`}
                      className={`w-12 h-12 flex items-center justify-center border transition-colors ${
                        selectedSize === size
                          ? 'bg-[#1C1917] text-white border-[#1C1917]'
                          : 'border-[#E5E0D8] hover:border-[#1C1917]'
                      }`}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Color Selection */}
            {product.colors?.length > 0 && (
              <div className="mb-6">
                <label className="block font-medium mb-3">{t({ it: 'Colore', en: 'Colour' })}: {selectedColor}</label>
                <div className="flex flex-wrap gap-3">
                  {product.colors.map((color) => (
                    <button
                      key={color}
                      onClick={() => setSelectedColor(color)}
                      data-testid={`color-${color}`}
                      className={`px-4 py-2 border transition-colors ${
                        selectedColor === color
                          ? 'bg-[#1C1917] text-white border-[#1C1917]'
                          : 'border-[#E5E0D8] hover:border-[#1C1917]'
                      }`}
                    >
                      {color}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Quantity */}
            <div className="mb-8">
              <label className="block font-medium mb-3">{t({ it: 'Quantità', en: 'Quantity' })}</label>
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  data-testid="quantity-decrease"
                  className="w-10 h-10 flex items-center justify-center border border-[#E5E0D8] hover:border-[#1C1917] transition-colors"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <span className="w-12 text-center font-medium" data-testid="quantity-display">{quantity}</span>
                <button
                  onClick={() => setQuantity(quantity + 1)}
                  data-testid="quantity-increase"
                  className="w-10 h-10 flex items-center justify-center border border-[#E5E0D8] hover:border-[#1C1917] transition-colors"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-4">
              <button
                onClick={handleAddToCart}
                disabled={addingToCart || product.stock === 0}
                data-testid="add-to-cart-btn"
                className="flex-1 btn-primary flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {addingToCart ? (
                  <span>Aggiunta in corso...</span>
                ) : product.stock === 0 ? (
                  <span>Esaurito</span>
                ) : (
                  <>
                    <ShoppingBag className="w-5 h-5" />
                    {t({ it: 'Aggiungi al Carrello', en: 'Add to Cart' })}
                  </>
                )}
              </button>
              <button
                onClick={handleToggleWishlist}
                data-testid="wishlist-btn"
                className={`w-14 h-14 flex items-center justify-center border transition-colors ${
                  isInWishlist
                    ? 'bg-[#C05746] text-white border-[#C05746]'
                    : 'border-[#E5E0D8] hover:border-[#C05746] hover:text-[#C05746]'
                }`}
              >
                <Heart className={`w-5 h-5 ${isInWishlist ? 'fill-current' : ''}`} />
              </button>
            </div>

            {/* Stock Status */}
            {product.stock > 0 && product.stock < 10 && (
              <p className="mt-4 text-[#C05746] text-sm">
                {t({ it: `Solo ${product.stock} pezzi rimasti!`, en: `Only ${product.stock} left in stock!` })}
              </p>
            )}

            {/* Features */}
            <div className="mt-12 pt-8 border-t border-[#E5E0D8]">
              <h3 className="font-medium mb-4">{t({ it: 'Caratteristiche', en: 'Features' })}</h3>
              <ul className="space-y-3">
                <li className="flex items-center gap-3 text-[#57534E]">
                  <Check className="w-5 h-5 text-[#4A5D23]" />
                  {t({ it: '100% Tessuto biologico certificato', en: '100% Certified organic fabric' })}
                </li>
                <li className="flex items-center gap-3 text-[#57534E]">
                  <Check className="w-5 h-5 text-[#4A5D23]" />
                  Made in Italy
                </li>
                <li className="flex items-center gap-3 text-[#57534E]">
                  <Check className="w-5 h-5 text-[#4A5D23]" />
                  {t({ it: 'Idoneo al programma Biolosophy Value', en: 'Eligible for the Biolosophy Value program' })}
                </li>
                <li className="flex items-center gap-3 text-[#57534E]">
                  <Check className="w-5 h-5 text-[#4A5D23]" />
                  {t({ it: 'Spedizione gratuita sopra €300', en: 'Free shipping over €300' })}
                </li>
              </ul>
            </div>
          </div>
        </div>

        <ProductReviews productId={productId} />
      </div>
    </div>
  );
}
